import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import produce from 'immer';
import set from 'lodash/set';

import { calculateNodePosition } from './nodesPositionHelper';
import { calculateNodesPositions, calculateNodesPositionsForTemplate } from './nodesHelper';
import generateAlphaNumericId from 'src/presentation/components/pages/ivr/helpers/generateAlphaNumericId';
import { IvrConfig } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import { IvrConfiguration } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import IvrNode from 'src/models/Ivrs/IvrNode';
import IvrTemplates, { IvrTemplate } from 'src/presentation/components/pages/ivr/constants/templates';
import { IvrTreeData } from 'src/models/Ivrs/IvrTreeData';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';
import { START_NODE_ID, DEFAULT_POSITION } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';

export const getConfigurationFromTemplate = (templateId, organization) => {
  const ivrTemplate = IvrTemplates[templateId] as IvrTemplate;
  const template = ivrTemplate.getTemplate({
    baseId: generateAlphaNumericId(),
    orgTimezone: organization!.timezone,
    orgUrl: organization!.gladlyUrl,
  });

  template.comment = `Add ${ivrTemplate.treeNodeLabel} template`;

  return template;
};

export const insertTemplate = ({ attr, config, parentId, organization, locationState, templateId }) => {
  const configuration = getConfigurationFromTemplate(templateId, organization);

  if (!parentId) {
    const newState = produce<IvrConfiguration>(config, draft => {
      set(draft, 'description', configuration.description);
      set(draft, 'rootNodeId', configuration.rootNodeId);
      set(draft, 'nodes', { ...draft.nodes, ...configuration.nodes });
    });

    const [nodesLocations] = calculateNodesPositions({ ivrConfig: new IvrConfig(newState) });
    return { positions: nodesLocations, config: newState };
  }

  const templateConfig = new IvrConfig(configuration);
  const templateNodesLocations = calculateNodesPositionsForTemplate({ locationState, parentId, templateConfig });

  const newState = produce(config, draft => {
    const [, , ...rest] = attr.split('.');
    const parentNodeAttr = parentId === START_NODE_ID ? attr : rest.join('.');

    const node = parentId === START_NODE_ID ? draft : find(draft.nodes, node => node.ivrNode.id === parentId);

    set(draft, 'nodes', [...(draft.nodes ? draft.nodes : []), ...configuration.nodes]);
    set(node, parentNodeAttr, templateConfig.rootNode.ivrNode.id);

    if (configuration.comment) {
      draft.comment = configuration.comment;
    }
  });

  return { positions: { ...locationState, ...templateNodesLocations }, config: newState };
};

export const getNewNodeBaseOnType = ({
  nodeType,
  nodeList,
}: {
  nodeType: NODE_TYPES;
  nodeList: IvrNode[];
}): IvrNode => {
  const nodeDetails: any = NODES_MAPPER[nodeType];

  const name = getUniqueName(nodeDetails.createNew.baseName, nodeList);
  let id = generateAlphaNumericId();
  if (nodeType === NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT) {
    id = `EDITABLE_${id}`;
    nodeType = NODE_TYPES.IVR_NODE_SCHEDULE;
  }
  return { type: nodeType, ivrNode: { id, name: name, ...nodeDetails.createNew.initial } };
};

export const getNewPositions = ({ newNodeId, positions, parentId, nodeList }) => {
  let parent: IvrNode = find(nodeList, node => node.ivrNode.id === parentId);

  if (!parent) {
    parent = {
      type: NODE_TYPES.START_NODE,
      ivrNode: {
        id: START_NODE_ID,
        name: NODES_MAPPER[NODE_TYPES.START_NODE].label,
        selection: '',
        description: NODES_MAPPER[NODE_TYPES.START_NODE].label,
      },
    };
  }
  let newPositions = cloneDeep(positions);
  if (isEmpty(newPositions)) {
    newPositions = { [START_NODE_ID]: DEFAULT_POSITION };
  }
  const nodePosition = calculateNodePosition({
    parentId: get(parent, 'ivrNode.id'),
    locationState: newPositions,
  });
  newPositions[newNodeId] = nodePosition;

  return newPositions;
};

type AddNewNodeToConfigurationProps = {
  attr: string;
  parentId: string;
  nodeType: NODE_TYPES;
  ivr: IvrConfiguration;
  positions: IvrTreeData;
};

export const addNewNodeToConfiguration = ({
  attr,
  parentId,
  nodeType,
  ivr,
  positions,
}: AddNewNodeToConfigurationProps) => {
  const nodeList = ivr.nodes;
  const newNode = getNewNodeBaseOnType({ nodeType, nodeList: nodeList });

  const newPositions = getNewPositions({ newNodeId: newNode.ivrNode.id, positions, parentId, nodeList });
  const newTreeData = { id: ivr.id, version: ivr.version, positions: newPositions };

  return {
    newNodeId: newNode.ivrNode.id,
    treeData: newTreeData,
    config: produce<IvrConfiguration>(ivr, (draft: IvrConfiguration) => {
      const [, , ...rest] = attr.split('.');
      const parentNodeAttr = parentId === START_NODE_ID ? attr : rest.join('.');

      if (!draft.nodes) {
        draft.nodes = [];
      }

      draft.nodes.push(newNode);

      if (parentId) {
        const node = parentId === START_NODE_ID ? draft : find(draft.nodes, node => node.ivrNode.id === parentId);
        set(node!, parentNodeAttr, newNode.ivrNode.id);
      }

      draft.comment = `add ${NODES_MAPPER[nodeType].treeNodeLabel} node`;
    }),
  };
};

const getUniqueName = (baseName: string, nodeList: IvrNode[]): string => {
  const checkIfExists = name => find(nodeList, (option: any) => get(option, 'ivrNode.name') === name);
  if (!checkIfExists(baseName)) {
    return baseName;
  }
  for (let index = 1; index < 100; index++) {
    if (!find(nodeList, (option: any) => option.ivrNode.name === `${baseName} ${index}`)) {
      return `${baseName} ${index}`;
    }
  }
  return baseName;
};
